/**
 * The breakers on one board, and how far each has got.
 *
 * Progress is two numbers because completion is two things: every check
 * answered and every required photo taken. A single percentage would let a
 * lineup read as done with an empty evidence pack.
 */

import { memo } from 'react';
import type { Asset } from '../domain/types.js';
import type { BreakerSummary } from '../domain/capture.js';

export interface LineupListProps {
  board: Asset;
  breakers: readonly Asset[];
  summaries: Readonly<Record<string, BreakerSummary>>;
  onOpen: (breaker: Asset) => void;
  onOpenBoardScript: () => void;
}

const refOf = (a: Asset): string => a.tag.split('/').pop() ?? a.tag;

export const LineupList = memo(function LineupList({
  board, breakers, summaries, onOpen, onOpenBoardScript,
}: LineupListProps) {
  const done = breakers.filter((b) => summaries[refOf(b)]?.complete).length;
  const photos = breakers.reduce(
    (acc, b) => {
      const s = summaries[refOf(b)];
      return { got: acc.got + (s?.shotsCaptured ?? 0), need: acc.need + (s?.shotsRequired ?? 0) };
    },
    { got: 0, need: 0 },
  );

  return (
    <section className="lineup" aria-label={`${board.tag} breakers`}>
      <header>
        <h2>{board.tag}</h2>
        <p data-testid="lineup-progress">
          {done} of {breakers.length} breaker{breakers.length === 1 ? '' : 's'} complete ·{' '}
          {photos.got} of {photos.need} photos
        </p>
      </header>

      <button type="button" className="row board-script" onClick={onOpenBoardScript}>
        <span className="t">Board script</span>
        <span className="d">The checks that belong to the board, not to a breaker</span>
      </button>

      {breakers.length === 0 ? (
        <p className="empty">No breakers recorded on this board yet.</p>
      ) : (
        <ul>
          {breakers.map((b) => {
            const ref = refOf(b);
            const s = summaries[ref];
            return (
              <li key={b.tag}>
                <button type="button" className="row" onClick={() => onOpen(b)} data-complete={s?.complete ?? false}>
                  <span className="t">
                    {ref} · {b.breakerType === 'N/A' ? 'type unknown' : b.breakerType}
                  </span>
                  <span className="d" data-testid={`summary-${ref}`}>
                    {s ? `${s.shotsCaptured}/${s.shotsRequired} photos` : 'not started'}
                    {b.model ? ` · ${b.model}` : ''}
                  </span>
                  <span className={`chip ${(s?.verdict ?? 'Pending').toLowerCase().replace('/', '')}`}>
                    {s?.complete ? s.verdict : 'Pending'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
});
